import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import styles from './LegalText.module.css';

const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;

const escapeHtml = (value) => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

const plainTextToHtml = (value) => value
  .split(/\n{2,}/)
  .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br />')}</p>`)
  .join('');

const splitUrlAndTrailingPunctuation = (value) => {
  let url = value;
  let trailing = '';

  while (url && /[),.;:!?]$/.test(url)) {
    trailing = `${url.slice(-1)}${trailing}`;
    url = url.slice(0, -1);
  }

  return { url, trailing };
};

const linkifyHtml = (html) => {
  if (typeof document === 'undefined') return html;

  const container = document.createElement('div');
  container.innerHTML = html;

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        const parentTag = node.parentElement?.tagName?.toLowerCase();

        if (!node.nodeValue || !URL_REGEX.test(node.nodeValue)) {
          URL_REGEX.lastIndex = 0;
          return NodeFilter.FILTER_REJECT;
        }

        URL_REGEX.lastIndex = 0;

        if (parentTag === 'a' || parentTag === 'script' || parentTag === 'style') {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );

  const textNodes = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    textNodes.push(currentNode);
    currentNode = walker.nextNode();
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue || '';
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    URL_REGEX.lastIndex = 0;

    while ((match = URL_REGEX.exec(text)) !== null) {
      const matchedValue = match[0];
      const start = match.index;

      if (start > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
      }

      const { url, trailing } = splitUrlAndTrailingPunctuation(matchedValue);

      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = url;
        fragment.appendChild(link);
      }

      if (trailing) {
        fragment.appendChild(document.createTextNode(trailing));
      }

      lastIndex = start + matchedValue.length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    node.parentNode?.replaceChild(fragment, node);
  });

  return container.innerHTML;
};

const LegalText = ({ data }) => {
  const rawContent = typeof data?.content === 'string'
    ? data.content.replace(/\r\n/g, '\n').trim()
    : '';

  const contentHtml = useMemo(() => {

    if (!rawContent) return '';

    const htmlSource = HTML_TAG_REGEX.test(rawContent)
      ? rawContent
      : plainTextToHtml(rawContent);

    const sanitizedHtml = DOMPurify.sanitize(htmlSource, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['img'],
    });

    return linkifyHtml(sanitizedHtml);
  }, [rawContent]);

  if (!contentHtml) return null;

  return (
    <section className={styles.legal}>
      <div className={styles.legal__container}>
        {data.title && <h2 className={styles.legal__title}>{data.title}</h2>}
        <div className={styles.legal__content} dangerouslySetInnerHTML={{ __html: contentHtml }} />
      </div>
    </section>
  );
};

export default LegalText;
